import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PartyPopper } from 'lucide-react';
import { portalApi } from '../lib/api';

interface PortalDeadline {
  id: string;
  obligationName: string;
  obligationCode: string;
  periodLabel: string;
  dueDate: string;
  status: string;
}

export function DeadlinesPage() {
  const { t } = useTranslation();
  const [deadlines, setDeadlines] = useState<PortalDeadline[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    portalApi
      .getDeadlines()
      .then((res: { data: PortalDeadline[] }) => setDeadlines(res.data))
      .catch(() => setError(t('portal.uploadError')))
      .finally(() => setLoading(false));
  }, [t]);

  const statusLabel = (status: string) => {
    const key = `portal.deadlineStatus.${status}`;
    const translated = t(key);
    return translated !== key ? translated : status;
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'status-review';
      case 'IN_PROGRESS': return 'status-new';
      case 'FILED': return 'status-ok';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const today = new Date();

  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-4">{t('portal.deadlines')}</h2>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-muted rounded-lg h-20" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-destructive text-center py-8">{error}</p>
      ) : !deadlines.length ? (
        <div className="text-center py-8">
          <div className="w-14 h-14 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto mb-4">
            <PartyPopper className="h-7 w-7 text-muted-foreground/40" />
          </div>
          <p className="text-sm text-muted-foreground">{t('portal.noDeadlines')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deadlines.map((dl) => {
            const due = new Date(dl.dueDate);
            const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            const isUrgent = daysLeft <= 7 && daysLeft >= 0 && dl.status === 'PENDING';

            return (
              <div
                key={dl.id}
                className={`bg-card rounded-2xl border border-border p-3 shadow-lifted hover:shadow-lifted-lg transition-shadow ${isUrgent ? 'border-warning' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{dl.obligationName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{dl.periodLabel}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold shrink-0 ${statusColor(dl.status)}`}>
                    {statusLabel(dl.status)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {due.toLocaleDateString('el-GR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  {dl.status === 'PENDING' && (
                    <span className={`text-xs ${daysLeft <= 3 ? 'text-destructive font-bold' : daysLeft <= 7 ? 'text-warning font-bold' : 'text-muted-foreground font-medium'}`}>
                      {daysLeft > 0
                        ? t(daysLeft === 1 ? 'portal.daysRemaining' : 'portal.daysRemaining_plural', { count: daysLeft })
                        : daysLeft === 0
                          ? t('portal.today')
                          : t(Math.abs(daysLeft) === 1 ? 'portal.daysOverdue' : 'portal.daysOverdue_plural', { count: Math.abs(daysLeft) })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
